import type { Query } from 'accesscontrol';
import type { AxiosError } from 'axios';
import type { GraphType } from 'api-client';
import type { AuthAccountWithPassType } from 'api-client/accounts';
import type { BriefAccessSavedType, BriefSavedType } from 'api-client/briefs';
import {
  bulkCreateOrUpdateBriefAccess,
  createOrUpdateBriefAccess,
  deleteBriefAccess,
  deleteOwnBriefAccess,
  retrieveBrief,
  retrieveBriefAccess,
  updateBrief,
} from 'api-client/briefs';
import type {
  ArtifactKey,
  AuditableType,
  BriefAccessRoleType,
  BriefAccessUser,
  Favorites,
  SnapshotData,
  TemplatableType,
} from 'bc-model';
import { extractPrincipal } from 'bc-model';
import getAccess from 'components/Auth/getAccess';
import apiAction, { handleApiLifecycle } from 'stores/actions/api';
import { getFeatureFlag } from 'featureFlags';
import { previewVersion } from 'stores/compare/versions';
import { immer } from 'zustand/middleware/immer';
import { type StoreSetter, type StoreApi, type Mutate, createStore } from 'zustand';
import { combine, devtools, subscribeWithSelector } from 'zustand/middleware';
import type { FC, PropsWithChildren } from 'react';
import { createContext, useContext } from 'react';
import { useStoreWithEqualityFn } from 'zustand/traditional';

interface BriefDataType extends GraphType, AuditableType, TemplatableType {
  id: number;
  briefId: number;
  accountId: number | null;
  key: string;
  title: string;
  repo: string | null;
  favorites: Favorites;
  structure: ArtifactKey[];
  deleted: boolean;
  readmeId: number | null;
  discoverable: boolean;
  starterPlan: boolean | null;
  isStarterPlanInstance: boolean | null;
  groupId: number | null;
  organizationId: number | null;
  description: string | null;
  snapshotData: SnapshotData | null;
  atoReferenceDateId: number | null;
  atoCodeOffset: number | null;
  briefAccess: {
    account: {
      [x: string | number]: BriefAccessSavedType;
    };
    group: {
      [x: number]: BriefAccessSavedType;
    };
    organization: {
      [x: number]: BriefAccessSavedType;
    };
  };
  briefOwner: BriefAccessSavedType;
  effectiveRole: BriefAccessRoleType;
  classification: string;
}

type BriefStateType = {
  brief: BriefDataType;
  effectiveAccess?: Query;
  canJoin: boolean;
  etag?: string;
  loading: { [key: string]: boolean };
  failure: { [key: string]: Error };
  calculated: any;
  allBriefDirectUsers: BriefAccessUser[];
  allBriefGroupsUsers: BriefAccessUser[];
  allBriefOrganizationsUsers: BriefAccessUser[];
  allBriefTeamUsers: BriefAccessUser[];
};

const initialState: BriefStateType = {
  brief: {} as BriefDataType,
  effectiveAccess: undefined,
  canJoin: false,
  etag: undefined,
  loading: { init: true },
  failure: {},
  calculated: {
    closure: [],
    scores: [],
  },
  allBriefTeamUsers: [],
  allBriefDirectUsers: [],
  allBriefGroupsUsers: [],
  allBriefOrganizationsUsers: [],
};

const getBriefAccessAndOwnerFromSavedType = (briefAccessSavedType: BriefAccessSavedType[]) => {
  let briefOwner;
  const briefAccess: BriefDataType['briefAccess'] = {
    account: {},
    group: {},
    organization: {},
  };
  briefAccessSavedType.forEach((accessRecord) => {
    if (accessRecord.role === 'owner') {
      briefOwner = accessRecord;
    } else if (accessRecord.accountId) {
      briefAccess.account[accessRecord.accountId] = accessRecord;
    } else if (accessRecord.accountUuid) {
      briefAccess.account[accessRecord.accountUuid] = accessRecord;
    } else if (accessRecord.groupId) {
      briefAccess.group[accessRecord.groupId] = accessRecord;
    } else if (accessRecord.organizationId) {
      briefAccess.organization[accessRecord.organizationId] = accessRecord;
    }
  });
  return { briefOwner, briefAccess };
};

/** Owner `account` join matches {@link AuthAccountWithPassType}; picker rows use {@link BriefAccessUser}. */
const briefAccessUserFromAuthAccount = (account: AuthAccountWithPassType): BriefAccessUser => ({
  id: account.id,
  uuid: account.uuid,
  name: account.name,
  avatarUrl: account.avatarUrl,
  avatarColor: account.avatarColor,
  firstName: account.firstName,
  lastName: account.lastName,
});

// Adding these to the store prevents the need to re-calculate them within component renders.
const computeDerivedAccessUsers = (
  state: Pick<
    BriefStateType,
    | 'allBriefDirectUsers'
    | 'allBriefGroupsUsers'
    | 'allBriefOrganizationsUsers'
    | 'allBriefTeamUsers'
  >,
  briefAccess: BriefDataType['briefAccess'],
  briefOwner: BriefAccessSavedType | undefined
): void => {
  state.allBriefDirectUsers = Object.values(briefAccess?.account ?? {})
    .map((user) => user?.account)
    .flat();
  state.allBriefGroupsUsers = Object.values(briefAccess?.group ?? {})
    .map((group) => group.group?.users)
    .filter((users) => !!users)
    .flat();
  state.allBriefOrganizationsUsers = Object.values(briefAccess?.organization ?? {})
    .map((org) => org?.organization?.users)
    .filter((users) => !!users)
    .flat();

  // For account owners add the account; for group/org owners spread their members (not the entity
  // itself — adding an org/group object with its numeric id would let it appear as a selectable
  // author and cause a random user to be shown when that id collides with a real account id).
  // If .users is absent (e.g. a lightweight API response), falls back to [] silently — members
  // will be missing from the picker until access is reloaded with the join populated.
  const ownerUsers: BriefAccessUser[] = briefOwner?.account
    ? [briefAccessUserFromAuthAccount(briefOwner.account)]
    : (briefOwner?.group?.users ?? briefOwner?.organization?.users ?? []);
  // TODO[PIBE-246]: collapse to single identifier once id columns are removed
  const allBriefTeamUsersSet = new Set<number | string>();
  state.allBriefTeamUsers = state.allBriefDirectUsers
    .concat(state.allBriefGroupsUsers)
    .concat(state.allBriefOrganizationsUsers)
    .concat(ownerUsers)
    .filter((account): account is BriefAccessUser => {
      const key = account?.uuid ?? account?.id;
      if (!key || allBriefTeamUsersSet.has(key)) {
        return false;
      }
      allBriefTeamUsersSet.add(key);
      return true;
    });
};

const briefStoreActions = (
  set: StoreSetter<BriefStateType>,
  get: StoreApi<BriefStateType>['getState']
) => {
  const loadBriefAction = async (
    owner?: string,
    briefKey?: string,
    briefId?: number
  ): Promise<BriefSavedType | null> => {
    const key = get()?.brief === initialState.brief ? 'init' : 'update';
    set((state) => {
      state.loading[key] = true;
      return state;
    });
    try {
      const retrieveBriefResp = await retrieveBrief(owner, briefKey, briefId);
      const { etag } = retrieveBriefResp.headers;
      const brief = retrieveBriefResp.data;
      const effectiveAccess = getAccess(brief.effectiveRole);

      set((state) => {
        state.effectiveAccess = effectiveAccess;
        state.brief.effectiveRole = brief.effectiveRole;
        if (etag !== state.etag) {
          state.brief = { ...state.brief, ...(brief ?? ({} as BriefDataType)) };
          state.etag = etag;
        }
        return state;
      });
      return brief;
    } catch (failure) {
      const res = (failure as AxiosError<{ canJoin?: boolean }>)?.response;
      if ([403, 404].includes(res?.status as number)) {
        set((state) => {
          state.canJoin = res?.data?.canJoin ?? false;
          return state;
        });
      }
      set((state) => {
        state.failure[key] = failure as Error;
        return state;
      });
    } finally {
      set((state) => {
        state.loading[key] = false;
        return state;
      });
    }

    return null;
  };
  // All actions
  return {
    // Legacy support for loading a brief by owner and key pair
    loadAccountOwnedBrief: async (owner?: string, briefKey?: string) =>
      loadBriefAction(owner, briefKey),
    // Load a brief by id
    loadBriefById: async (briefId?: number) => loadBriefAction(undefined, undefined, briefId),
    reload: apiAction(
      retrieveBrief,
      handleApiLifecycle(set, 'brief', (state, partialBrief, _ignored, etag) => {
        const effectiveAccess = getAccess(partialBrief.effectiveRole);
        Object.assign(state.brief, partialBrief);
        state.effectiveAccess = effectiveAccess;
        state.etag = etag;
      })
    ),
    unload: () => {
      set(() => {
        return initialState;
      });
    },
    // Access actions

    loadAccess: apiAction(
      retrieveBriefAccess,
      handleApiLifecycle(set, 'brief_access', (state, briefAccessRaw) => {
        const { briefAccess, briefOwner } = getBriefAccessAndOwnerFromSavedType(briefAccessRaw);
        state.brief.briefAccess = briefAccess;
        state.brief.briefOwner = briefOwner;
        computeDerivedAccessUsers(state, briefAccess, briefOwner);
      })
    ),
    bulkCreateOrUpdateAccess: apiAction(
      bulkCreateOrUpdateBriefAccess,
      handleApiLifecycle(set, 'brief_access', (state, { briefAccess }) => {
        state.brief.briefAccess = { account: {}, group: {}, organization: {} };
        briefAccess.forEach((accessRecord) => {
          if (accessRecord.role === 'owner') {
            state.brief.briefOwner = accessRecord;
          } else if (accessRecord.accountId) {
            state.brief.briefAccess.account[accessRecord.accountId] = accessRecord;
          } else if (accessRecord.accountUuid) {
            state.brief.briefAccess.account[accessRecord.accountUuid] = accessRecord;
          } else if (accessRecord.groupId) {
            state.brief.briefAccess.group[accessRecord.groupId] = accessRecord;
          } else if (accessRecord.organizationId) {
            state.brief.briefAccess.organization[accessRecord.organizationId] = accessRecord;
          }
        });
        computeDerivedAccessUsers(state, state.brief.briefAccess, state.brief.briefOwner);
      })
    ),
    createOrUpdateAccess: apiAction(
      async (asUser, briefAccess) => {
        const useUuid = !!getFeatureFlag('USE_ACCOUNT_UUID');
        const principal = extractPrincipal(briefAccess, useUuid);
        return createOrUpdateBriefAccess(asUser, {
          briefId: briefAccess.briefId,
          role: briefAccess.role,
          principal,
        });
      },
      handleApiLifecycle(set, 'brief_access', (state, { briefAccess }) => {
        state.brief.briefAccess = {
          account: {},
          group: {},
          organization: {},
        };
        briefAccess.forEach((accessRecord) => {
          if (accessRecord.role === 'owner') {
            state.brief.briefOwner = accessRecord;
          } else if (accessRecord.accountId) {
            state.brief.briefAccess.account[accessRecord.accountId] = accessRecord;
          } else if (accessRecord.accountUuid) {
            state.brief.briefAccess.account[accessRecord.accountUuid] = accessRecord;
          } else if (accessRecord.groupId) {
            state.brief.briefAccess.group[accessRecord.groupId] = accessRecord;
          } else if (accessRecord.organizationId) {
            state.brief.briefAccess.organization[accessRecord.organizationId] = accessRecord;
          }
        });
        const { briefAccess: ba, briefOwner: bo } = state.brief;
        computeDerivedAccessUsers(state, ba, bo);
      })
    ),
    deleteAccess: apiAction(
      deleteBriefAccess,
      handleApiLifecycle(set, 'brief_access', (state, accessRecord) => {
        if (accessRecord?.accountId != null) {
          delete state.brief.briefAccess.account[accessRecord.accountId];
        } else if (accessRecord?.accountUuid != null) {
          delete state.brief.briefAccess.account[accessRecord.accountUuid];
        } else if (accessRecord?.groupId != null) {
          delete state.brief.briefAccess.group[accessRecord.groupId];
        } else if (accessRecord?.organizationId != null) {
          delete state.brief.briefAccess.organization[accessRecord.organizationId];
        }
        const { briefAccess, briefOwner } = state.brief;
        computeDerivedAccessUsers(state, briefAccess, briefOwner);
      })
    ),
    deleteOwnAccess: apiAction(
      deleteOwnBriefAccess,
      handleApiLifecycle(set, 'brief_access', (state, accessRecord) => {
        if (accessRecord?.accountId != null) {
          delete state.brief.briefAccess.account[accessRecord.accountId];
        } else if (accessRecord?.accountUuid != null) {
          delete state.brief.briefAccess.account[accessRecord.accountUuid];
        } else if (accessRecord?.groupId != null) {
          delete state.brief.briefAccess.group[accessRecord.groupId];
        } else if (accessRecord?.organizationId != null) {
          delete state.brief.briefAccess.organization[accessRecord.organizationId];
        }
        const { briefAccess, briefOwner } = state.brief;
        computeDerivedAccessUsers(state, briefAccess, briefOwner);
      })
    ),
    removeAccessRecord: (payload: {
      accountId?: number;
      accountUuid?: string;
      groupId?: number;
      organizationId?: number;
    }) => {
      set((state) => {
        if (payload.accountId) {
          delete state.brief.briefAccess.account[payload.accountId];
        } else if (payload.accountUuid) {
          delete state.brief.briefAccess.account[payload.accountUuid];
        } else if (payload.groupId) {
          delete state.brief.briefAccess.group[payload.groupId];
        } else if (payload.organizationId) {
          delete state.brief.briefAccess.organization[payload.organizationId];
        }

        const { briefAccess, briefOwner } = state.brief;
        computeDerivedAccessUsers(state, briefAccess, briefOwner);
      });
    },
    // Brief actions
    updateBrief: apiAction(
      updateBrief,
      handleApiLifecycle(
        set,
        'brief',
        (state, briefUpdated) => {
          Object.assign(state.brief, briefUpdated);
        },
        (state, briefUpdating) => {
          Object.assign(state.brief, { ...briefUpdating }, previewVersion(state.brief));
        }
      )
    ),
  };
};

type BriefStoreActionsType = ReturnType<typeof briefStoreActions>;

type BriefStoreType = BriefStateType & BriefStoreActionsType;

type BriefStore = Mutate<
  StoreApi<BriefStoreType>,
  [['zustand/immer', never], ['zustand/subscribeWithSelector', never], ['zustand/devtools', never]]
>;

const createBriefStore = (): BriefStore => {
  return createStore<BriefStoreType>()(
    devtools(subscribeWithSelector(immer(combine(initialState, briefStoreActions))), {
      name: 'briefStore',
    })
  );
};

const globalBriefStore = createBriefStore();

const BriefStoreContext = createContext<BriefStore | null>(null);

interface BriefStoreProviderProps extends PropsWithChildren {
  store: BriefStore;
}

const BriefStoreContextProvider: FC<BriefStoreProviderProps> = ({ children, store }) => (
  <BriefStoreContext.Provider value={store}>{children}</BriefStoreContext.Provider>
);

const useBriefStoreApi = (): BriefStore => {
  const store = useContext(BriefStoreContext);
  return store ?? globalBriefStore;
};

function useBriefStore(): BriefStoreType;
function useBriefStore<T>(
  selector: (state: BriefStoreType) => T,
  equalityFn?: (a: T, b: T) => boolean
): T;
function useBriefStore<T>(
  selector: (state: BriefStoreType) => T = (state) => state as unknown as T,
  equalityFn?: (a: T, b: T) => boolean
): T {
  const store = useContext(BriefStoreContext);
  if (!store) {
    throw new Error('Missing BriefStoreContext provider');
  }
  return useStoreWithEqualityFn(store, selector, equalityFn);
}
// Calculated fields
// TODO implement calculated fields as zustand middleware

export default useBriefStore;
export {
  BriefStoreContextProvider,
  createBriefStore,
  useBriefStoreApi,
  globalBriefStore,
  getBriefAccessAndOwnerFromSavedType,
};
export type { BriefStateType, BriefDataType, BriefStoreType, BriefStore };
